import { useState, useEffect, useCallback } from "react";
import { makeTr } from "@/i18n/tr";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import axios from "axios";
import { 
  Trophy, 
  Users, 
  Clock, 
  Coins,
  Crown,
  Swords,
  ArrowRight,
  Loader2,
  Play,
  RefreshCw
} from "lucide-react";
import { API_BASE as API_URL } from "@/config/api";

export default function TournamentDetailPage() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const tr = makeTr(i18n.language);
  const { user, token } = useStore();
  
  const [tournament, setTournament] = useState(null);
  const [standings, setStandings] = useState([]);
  const [pairings, setPairings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [findingGame, setFindingGame] = useState(false);
  const [activeTab, setActiveTab] = useState("standings");

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadTournament();
    // Refresh every 10 seconds for live updates
    const interval = setInterval(loadTournament, 10000);
    return () => clearInterval(interval);
  }, [tournamentId]);

  const loadTournament = async () => {
    try {
      const [tournamentRes, standingsRes, pairingsRes] = await Promise.all([
        axios.get(`${API_URL}/tournaments/${tournamentId}`),
        axios.get(`${API_URL}/tournaments/${tournamentId}/standings`).catch(() => ({ data: { standings: [] } })),
        axios.get(`${API_URL}/tournaments/${tournamentId}/pairings`).catch(() => ({ data: { pairings: [] } }))
      ]);
      
      setTournament(tournamentRes.data);
      setStandings(standingsRes.data.standings || []);
      setPairings(pairingsRes.data.pairings || []);
    } catch (err) {
      console.error("Failed to load tournament:", err);
      toast.error("Failed to load tournament");
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    setJoining(true);
    try {
      await axios.post(tournament?.is_auto ? `${API_URL}/tournaments/auto/${tournamentId}/join` : `${API_URL}/tournaments/${tournamentId}/join`, {}, { headers });
      toast.success(tr("Вы успешно зарегистрировались!", "You are registered!"));
      loadTournament();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join");
    } finally {
      setJoining(false);
    }
  };

  const handleFindArenaGame = async () => {
    setFindingGame(true);
    try {
      const res = await axios.post(`${API_URL}/arena/${tournamentId}/find-game`, {}, { headers });
      if (res.data.game_id) {
        navigate(`/game/${res.data.game_id}`);
      } else {
        toast.info(tr("Ищем соперника...", "Searching for an opponent..."));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to find game");
    } finally {
      setFindingGame(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m + ${secs}s` : `${mins}m`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const isRegistered = Boolean(tournament?.is_registered) || (tournament?.standings || []).some((p) => p.user_id === user?.id);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Trophy className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-xl font-bold">{tr("Турнир не найден", "Tournament not found")}</h2>
        <Button onClick={() => navigate("/tournaments")} className="mt-4">
          {tr("Вернуться к турнирам", "Back to tournaments")}
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl" data-testid="tournament-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <Trophy className="h-8 w-8 text-primary" />
            <h1 className="text-2xl font-black">{tournament.name}</h1>
            <Badge variant={tournament.format === "swiss" ? "default" : "secondary"}>
              {tournament.format === "swiss" ? "Swiss" : "Arena"}
            </Badge>
            <Badge variant={
              tournament.status === "registration" ? "outline" :
              tournament.status === "active" ? "default" : "secondary"
            }>
              {tournament.status === "registration" ? tr("Регистрация", "Registration") :
               tournament.status === "active" ? tr("Идёт", "Live") : tr("Завершён", "Finished")}
            </Badge>
          </div>
          {tournament.description && (
            <p className="text-muted-foreground mt-2">{tournament.description}</p>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          {tournament.status === "registration" && !isRegistered && (
            <Button onClick={handleJoin} disabled={joining} className="btn-gold">
              {joining ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Crown className="h-4 w-4 mr-2" />}
              {tournament.entry_fee > 0 ? `${tr("Участвовать", "Join")} (${tournament.entry_fee} Points)` : tr("Участвовать", "Join")}
            </Button>
          )}
          {tournament.status === "active" && tournament.format === "arena" && isRegistered && (
            <Button onClick={handleFindArenaGame} disabled={findingGame} className="btn-gold">
              {findingGame ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Swords className="h-4 w-4 mr-2" />}
              {tr("Найти соперника", "Find opponent")}
            </Button>
          )}
          <Button variant="outline" onClick={loadTournament}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {tr("Обновить", "Refresh")}
          </Button>
        </div>
      </div>

      {/* Tournament Info */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="glass">
          <CardContent className="p-4 text-center">
            <Users className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
            <p className="text-2xl font-bold">{tournament.current_players}</p>
            <p className="text-xs text-muted-foreground">/ {tournament.max_players} {tr("игроков", "players")}</p>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-4 text-center">
            <Clock className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
            <p className="text-2xl font-bold">{formatTime(tournament.time_control)}</p>
            <p className="text-xs text-muted-foreground">{tr("контроль времени", "time control")}</p>
          </CardContent>
        </Card>
        {tournament.format === "swiss" && (
          <Card className="glass">
            <CardContent className="p-4 text-center">
              <Trophy className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
              <p className="text-2xl font-bold">{tournament.current_round}/{tournament.rounds}</p>
              <p className="text-xs text-muted-foreground">{tr("раунд", "round")}</p>
            </CardContent>
          </Card>
        )}
        <Card className="glass">
          <CardContent className="p-4 text-center">
            <Coins className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
            <p className="text-2xl font-bold">{tournament.entry_fee > 0 ? `${tournament.entry_fee} Points` : "Free"}</p>
            <p className="text-xs text-muted-foreground">{tr("взнос", "entry fee")}</p>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-4 text-center">
            <Crown className="h-5 w-5 mx-auto text-primary mb-1" />
            <p className="text-2xl font-bold text-primary">{tournament.prize_pool.toFixed(0)} Points</p>
            <p className="text-xs text-muted-foreground">{tr("призовой фонд", "prize pool")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md mb-6">
          <TabsTrigger value="standings">
            <Trophy className="h-4 w-4 mr-2" />
            {tr("Таблица", "Standings")}
          </TabsTrigger>
          <TabsTrigger value="pairings">
            <Swords className="h-4 w-4 mr-2" />
            {tr("Пейринги", "Pairings")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="standings">
          <Card className="glass">
            <CardHeader>
              <CardTitle>{tr("Турнирная таблица", "Tournament standings")}</CardTitle>
            </CardHeader>
            <CardContent>
              {standings.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  {tr("Турнир ещё не начался", "Tournament has not started yet")}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>{tr("Игрок", "Player")}</TableHead>
                      <TableHead className="text-center">{tr("Очки", "Points")}</TableHead>
                      <TableHead className="text-center">W</TableHead>
                      <TableHead className="text-center">D</TableHead>
                      <TableHead className="text-center">L</TableHead>
                      <TableHead className="text-center">{tr("Игр", "Games")}</TableHead>
                      {tournament.format === "swiss" && (
                        <TableHead className="text-center">{tr("Бухгольц", "Buchholz")}</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {standings.map((player, index) => (
                      <TableRow 
                        key={player.user_id}
                        className={player.user_id === user?.id ? "bg-primary/10" : ""}
                      >
                        <TableCell className="font-bold">
                          {index < 3 ? (
                            <span className={
                              index === 0 ? "text-yellow-500" :
                              index === 1 ? "text-gray-400" : "text-amber-600"
                            }>
                              {index === 0 ? "🥇" : index === 1 ? "🥈" : "🥉"}
                            </span>
                          ) : (
                            index + 1
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{player.username}</span>
                            {player.user_id === user?.id && (
                              <Badge variant="outline" className="text-xs">{tr("Вы", "You")}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-bold text-primary">
                          {player.points}
                        </TableCell>
                        <TableCell className="text-center text-green-500">{player.wins}</TableCell>
                        <TableCell className="text-center text-yellow-500">{player.draws}</TableCell>
                        <TableCell className="text-center text-red-500">{player.losses}</TableCell>
                        <TableCell className="text-center">{player.games_played}</TableCell>
                        {tournament.format === "swiss" && (
                          <TableCell className="text-center text-muted-foreground">
                            {player.buchholz?.toFixed(1) || "0.0"}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pairings">
          <Card className="glass">
            <CardHeader>
              <CardTitle>
                {tournament.format === "swiss" 
                  ? `{tr("Раунд", "Round")} ${tournament.current_round || 1}` 
                  : tr("Текущие игры", "Current games")
                }
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pairings.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  {tournament.status === "registration" 
                    ? tr("Пейринги появятся после начала турнира", "Pairings will appear once the tournament starts")
                    : tr("Нет активных игр", "No active games")
                  }
                </div>
              ) : (
                <div className="space-y-3">
                  {pairings.map((pairing, index) => (
                    <div 
                      key={pairing.game_id || index}
                      className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border/50"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-6 h-6 rounded bg-white border flex items-center justify-center">
                          <span className="text-xs">⬜</span>
                        </div>
                        <span className={`font-medium ${pairing.winner === "white" ? "text-primary" : ""}`}>
                          {pairing.white_player}
                        </span>
                      </div>
                      
                      <div className="px-4 font-mono">
                        {pairing.status === "pending" ? (
                          <Badge variant="outline">{tr("Ожидание", "Waiting")}</Badge>
                        ) : pairing.status === "active" ? (
                          <Badge className="bg-green-500">{tr("Идёт", "Live")}</Badge>
                        ) : pairing.status === "bye" ? (
                          <Badge variant="secondary">1-0</Badge>
                        ) : (
                          <Badge variant="secondary">{pairing.result}</Badge>
                        )}
                      </div>

                      <div className="flex items-center gap-3 flex-1 justify-end">
                        <span className={`font-medium ${pairing.winner === "black" ? "text-primary" : ""}`}>
                          {pairing.black_player}
                        </span>
                        <div className="w-6 h-6 rounded bg-gray-800 border flex items-center justify-center">
                          <span className="text-xs">⬛</span>
                        </div>
                      </div>

                      {pairing.game_id && pairing.status === "active" && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="ml-4"
                          onClick={() => navigate(`/game/${pairing.game_id}`)}
                        >
                          <Play className="h-3 w-3 mr-1" />
                          {tr("Смотреть", "Watch")}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
